import copy from "copy-to-clipboard";
import { Building2, Check, Copy, Flag } from "lucide-react";
import moment from "moment";
import * as React from "react";
import { Link } from "react-router-dom";

import { Svg } from "@/assets";
import { Badge, Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import { User, VideoProject } from "@/services/bedrock";
import { URLS } from "@/utils";
import { toast } from "sonner";

import {
  VideoProjectStaffPicker,
  canUnassignStaff,
  recommendedStaffForRole,
  resolveStaffUser,
} from "@/components/VideoProjectStaffPicker";
import {
  VideoProjectQaBadges,
  VideoProjectRevisionBadge,
  VideoProjectStatusPicker,
} from "@/components/VideoProjectStatusPicker";
import { mediaUrlIdentity } from "./kanbanColumnQuery";
import { KanbanColumnId, showKanbanPriorityAccent } from "./kanbanColumns";

interface VideoProjectKanbanCardProps {
  videoProject: VideoProject;
  staffUsers: User[];
  currentUserId?: number;
  disabled?: boolean;
  selected?: boolean;
  onAssigneeChange?: (project: VideoProject, userId: number | null) => void;
  onQaChange?: (project: VideoProject, userId: number | null) => void;
  onStatusChange?: (project: VideoProject, columnId: KanbanColumnId) => void;
}

const KanbanProjectThumbnail = React.memo(function KanbanProjectThumbnail({ src }: { src?: string | null }) {
  const nextSrc = src || Svg.PromptPlaceholder;
  const displaySrcRef = React.useRef(nextSrc);
  if (mediaUrlIdentity(displaySrcRef.current) !== mediaUrlIdentity(nextSrc)) {
    displaySrcRef.current = nextSrc;
  }

  return (
    <img
      src={displaySrcRef.current}
      alt=""
      className={cn("size-full", src ? "object-cover object-center" : "object-none object-center")}
    />
  );
});

function cardDateLabel(videoProject: VideoProject) {
  const date = videoProject.createdDate
    ? moment(videoProject.createdDate)
    : videoProject.statusDate
    ? moment(videoProject.statusDate)
    : null;
  if (!date) return null;

  const prefix = videoProject.createdDate ? "Created" : "Updated";
  const yearCutoff = moment().add(1, "month").startOf("month").subtract(1, "year");
  const dateFormat = date.isBefore(yearCutoff) ? "MMM D, YYYY" : "MMM D";
  return {
    label: `${prefix} ${date.format(dateFormat)}`,
    title: date.format("MMM D, YYYY [at] h:mm a"),
  };
}

export const VideoProjectKanbanCard = React.memo(function VideoProjectKanbanCard({
  videoProject,
  staffUsers,
  currentUserId,
  disabled = false,
  selected = false,
  onAssigneeChange,
  onQaChange,
  onStatusChange,
}: VideoProjectKanbanCardProps) {
  const [copiedId, setCopiedId] = React.useState(false);
  const assignments = videoProject.queueAssignments ?? [];
  const assignee = resolveStaffUser(videoProject.editorUserId, staffUsers, assignments);
  const qaUser = resolveStaffUser(videoProject.qaUserId, staffUsers, assignments);
  const recommendedEditors = recommendedStaffForRole(videoProject, "EDITOR", staffUsers);
  const recommendedQa = recommendedStaffForRole(videoProject, "QA", staffUsers);
  const dateLabel = cardDateLabel(videoProject);
  const isPriority = Boolean(videoProject.isPriority);
  const showPriorityAccent = showKanbanPriorityAccent(videoProject.status, videoProject.isPriority);
  const qaFailed = videoProject.qaStatus === "REJECTED";
  const hasRevisions = Boolean(videoProject.revisionCount && videoProject.revisionCount > 0);
  const hasQaFailures = Boolean(videoProject.qaFailureCount && videoProject.qaFailureCount > 0);

  const handleCopyId = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!copy(String(videoProject.id))) return;
    toast.success("Project ID copied to clipboard");
    setCopiedId(true);
    window.setTimeout(() => setCopiedId(false), 1500);
  };

  return (
    <Card
      size="sm"
      disabled={disabled}
      data-selected={selected ? "" : undefined}
      className={cn(
        "relative gap-0 overflow-hidden data-[size=sm]:py-0 ring-foreground/10 transition-[box-shadow,ring-color,ring-width] duration-200",
        selected ? "z-10 shadow-md ring-2 ring-ring/50" : "hover:shadow-md hover:ring-foreground/20",
        showPriorityAccent &&
          "before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-[inherit] before:bg-sky-500",
        videoProject.needsAck && (selected ? "ring-2 ring-amber-500" : "ring-amber-500")
      )}
    >
      <Link
        to={URLS.editor.videoProjects.detail.replace(":id", `${videoProject.id}`)}
        aria-label={videoProject.playerName || `Video project ${videoProject.id}`}
        className={cn("absolute inset-0 z-0", disabled && "pointer-events-none")}
      />
      <div className="relative z-10 flex flex-col gap-2 p-2.5 pointer-events-none">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground flex min-w-0 items-center gap-1 font-mono text-[11px]">
            {isPriority ? (
              <span title="Priority">
                <Flag className="size-3 shrink-0 fill-sky-500 text-sky-500" aria-hidden="true" />
              </span>
            ) : null}
            #{videoProject.id}
            <button
              type="button"
              onPointerDown={event => event.stopPropagation()}
              onClick={handleCopyId}
              className="pointer-events-auto text-muted-foreground hover:text-foreground -m-0.5 shrink-0 cursor-pointer rounded p-0.5 hover:bg-muted"
              title={copiedId ? "Copied" : "Copy project ID"}
              aria-label={copiedId ? "Project ID copied" : "Copy project ID"}
            >
              {copiedId ? (
                <Check className="size-3 text-emerald-600" strokeWidth={1.5} />
              ) : (
                <Copy className="size-3" strokeWidth={1.5} />
              )}
            </button>
          </span>
          <div className="flex shrink-0 items-center gap-1.5">
            <VideoProjectStaffPicker
              role="EDITOR"
              compact
              selectedUserId={videoProject.editorUserId}
              selectedUser={assignee}
              otherRoleUserId={videoProject.qaUserId}
              recommendedUsers={recommendedEditors}
              staffUsers={staffUsers}
              currentUserId={currentUserId}
              disabled={disabled}
              allowUnassign={canUnassignStaff("EDITOR", videoProject.status)}
              onSelect={userId => onAssigneeChange?.(videoProject, userId)}
            />
            <VideoProjectStaffPicker
              role="QA"
              compact
              selectedUserId={videoProject.qaUserId}
              selectedUser={qaUser}
              otherRoleUserId={videoProject.editorUserId}
              recommendedUsers={recommendedQa}
              staffUsers={staffUsers}
              currentUserId={currentUserId}
              disabled={disabled}
              allowUnassign={canUnassignStaff("QA", videoProject.status)}
              onSelect={userId => onQaChange?.(videoProject, userId)}
            />
          </div>
        </div>

        <div className="flex items-start gap-2.5">
          <div className="bg-muted size-16 shrink-0 overflow-hidden rounded-md">
            <KanbanProjectThumbnail src={videoProject.thumbnailUrl} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-start gap-1">
              <VideoProjectStatusPicker
                status={videoProject.status}
                statusDate={videoProject.statusDate}
                archived={!!videoProject.archived}
                requiresQa={videoProject.requiresQa}
                currentUserId={currentUserId}
                disabled={disabled}
                onSelect={columnId => onStatusChange?.(videoProject, columnId)}
              />
              <div className="text-foreground line-clamp-2 min-w-0 pt-0.5 text-left text-sm leading-snug font-medium tracking-tight">
                {videoProject.playerName || "Untitled Player"}
              </div>
            </div>
            {videoProject.companyName || videoProject.isTestProject ? (
              <div className="flex min-w-0 flex-wrap items-center gap-1">
                {videoProject.isTestProject ? (
                  <Badge
                    variant="outline"
                    className="h-5 gap-1 border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-800 shadow-none"
                    title="Test/demo project"
                  >
                    Demo
                  </Badge>
                ) : null}
                {videoProject.companyName ? (
                  <Badge
                    variant="outline"
                    className="text-muted-foreground h-5 max-w-full gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none"
                    title={videoProject.companyName}
                  >
                    <Building2 className="size-2.5 shrink-0 text-orange-500" />
                    <span className="truncate">{videoProject.companyName}</span>
                  </Badge>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {dateLabel || hasRevisions || hasQaFailures ? (
          <div className="flex items-center justify-between gap-2">
            {dateLabel ? (
              <div
                className="text-muted-foreground pointer-events-auto min-w-0 truncate text-[11px]"
                title={dateLabel.title}
              >
                {dateLabel.label}
              </div>
            ) : null}
            {hasRevisions || hasQaFailures ? (
              <div className="ml-auto flex shrink-0 items-center gap-1">
                <VideoProjectRevisionBadge className="pointer-events-auto" revisionCount={videoProject.revisionCount} />
                <VideoProjectQaBadges
                  className="pointer-events-auto"
                  qaFailed={qaFailed}
                  qaFailureCount={videoProject.qaFailureCount}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
});
